<script setup>
import { computed } from 'vue';

const props = defineProps({
  status: { type: String, required: true },
  title: { type: String, required: true },
  headline: { type: String, required: true },
  message: { type: String, default: '' },
  hint: { type: String, default: 'Calid is on watch even when the kit misbehaves.' },
  theme: { type: String, default: 'neutral' },
  actions: { type: Array, default: () => [] },
  timestamp: { type: String, default: () => new Date().toISOString() }
});

const themeClass = computed(() => `theme-${props.theme || 'neutral'}`);
</script>

<template>
  <div class="calid-error" :class="themeClass">
    <div class="bg-grid"></div>
    <div class="orb orb-a"></div>
    <div class="orb orb-b"></div>
    <div class="orb orb-c"></div>
    <div class="scan"></div>

    <div class="card">
      <div class="status">
        <span class="pill">HTTP {{ status }}</span>
        <span class="spark"></span>
      </div>
      <h1>{{ title }}</h1>
      <p class="headline">{{ headline }}</p>
      <p v-if="message" class="message">{{ message }}</p>

      <div v-if="actions.length" class="actions">
        <button
          v-for="(action, index) in actions"
          :key="index"
          type="button"
          :class="['btn', action.variant || 'primary']"
          @click="action.onClick"
        >
          <i v-if="action.icon" :class="action.icon"></i>
          <span>{{ action.label }}</span>
        </button>
      </div>

      <div class="meta">
        <span>{{ hint }}</span>
        <span class="dot">•</span>
        <span>{{ timestamp }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.calid-error {
  --bg-start: #0b1224;
  --bg-end: #0f172a;
  --accent: #38bdf8;
  --accent-2: #8b5cf6;
  --glow: rgba(56, 189, 248, 0.25);
  --text-main: #e2e8f0;
  --text-sub: #94a3b8;
  --panel: rgba(15, 23, 42, 0.75);
  --border: rgba(148, 163, 184, 0.25);
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 20% 20%, rgba(56, 189, 248, 0.08), transparent 30%),
    radial-gradient(circle at 80% 10%, rgba(139, 92, 246, 0.08), transparent 35%),
    linear-gradient(135deg, var(--bg-start), var(--bg-end));
  color: var(--text-main);
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  opacity: 0.5;
  animation: gridGlow 6s ease-in-out infinite;
}

@keyframes gridGlow {
  0%, 100% { opacity: 0.35; }
  50% { opacity: 0.65; }
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.7;
  mix-blend-mode: screen;
  animation: float 14s ease-in-out infinite;
}

.orb-a { width: 420px; height: 420px; top: -12%; left: -8%; background: radial-gradient(circle, var(--glow), transparent 70%); }
.orb-b { width: 320px; height: 320px; bottom: -15%; right: -10%; background: radial-gradient(circle, rgba(139, 92, 246, 0.2), transparent 70%); animation-delay: -5s; }
.orb-c { width: 260px; height: 260px; top: 45%; left: 60%; background: radial-gradient(circle, rgba(255, 255, 255, 0.08), transparent 70%); animation-delay: -9s; }

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  30% { transform: translate(24px, -18px) scale(1.05); }
  60% { transform: translate(-18px, 22px) scale(0.97); }
}

.scan {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.05), transparent);
  animation: scanMove 5s linear infinite;
  mix-blend-mode: screen;
}

@keyframes scanMove {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
}

.card {
  position: relative;
  width: min(960px, 100%);
  padding: clamp(1.75rem, 3vw, 2.5rem);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
  border: 1px solid var(--border);
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.02),
    0 0 40px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(16px);
  z-index: 1;
}

.status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text-sub);
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.spark {
  width: 34px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  filter: drop-shadow(0 0 10px var(--glow));
  animation: pulse 1.8s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.4; transform: scaleX(0.9); }
  50% { opacity: 1; transform: scaleX(1.1); }
}

h1 {
  font-size: clamp(2.3rem, 3vw, 2.9rem);
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
}

.headline {
  font-size: 1.1rem;
  color: var(--text-main);
  margin: 0 0 1rem;
  line-height: 1.6;
}

.message {
  color: var(--text-sub);
  line-height: 1.6;
  margin: 0 0 1.75rem;
  font-size: 1rem;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.35rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text-main);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn i { font-size: 1rem; }

.btn.primary {
  background: linear-gradient(135deg, var(--accent), var(--accent-2));
  border-color: rgba(255, 255, 255, 0.08);
  color: #0b1224;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35), 0 0 25px var(--glow);
}

.btn.primary:hover { transform: translateY(-1px) scale(1.01); box-shadow: 0 18px 36px rgba(0, 0, 0, 0.4), 0 0 35px var(--glow); }

.btn.ghost:hover { border-color: rgba(255, 255, 255, 0.35); background: rgba(255, 255, 255, 0.06); }

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  color: var(--text-sub);
  font-size: 0.95rem;
}

.dot { color: rgba(148, 163, 184, 0.7); }

/* Theme variants */
.theme-fire {
  --bg-start: #1b0a0a;
  --bg-end: #240f0f;
  --accent: #fb923c;
  --accent-2: #ef4444;
  --glow: rgba(239, 68, 68, 0.35);
}

.theme-ice {
  --bg-start: #0c1728;
  --bg-end: #0f2034;
  --accent: #38bdf8;
  --accent-2: #a5f3fc;
  --glow: rgba(56, 189, 248, 0.3);
}

.theme-lock {
  --bg-start: #0c101c;
  --bg-end: #0f1627;
  --accent: #22d3ee;
  --accent-2: #0ea5e9;
  --glow: rgba(14, 165, 233, 0.3);
}

.theme-glitch {
  --bg-start: #0d0b1a;
  --bg-end: #111827;
  --accent: #8b5cf6;
  --accent-2: #14f1d9;
  --glow: rgba(20, 241, 217, 0.32);
}

.theme-maintenance {
  --bg-start: #111418;
  --bg-end: #151b22;
  --accent: #fbbf24;
  --accent-2: #f97316;
  --glow: rgba(251, 191, 36, 0.28);
}

@media (max-width: 640px) {
  .calid-error { padding: 1.25rem; }
  .card { padding: 1.25rem; border-radius: 18px; }
  .actions { flex-direction: column; align-items: stretch; }
  .btn { justify-content: center; width: 100%; }
}
</style>
